import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { getUserPermissions, processUserPermissions } from '@apis/mantenedores';
import { useUsuariosManagement } from '../../modules/Mantenedores/hooks/useUsuariosManagement';

/**
 * Hook personalizado para manejar permisos de usuario
 * Automáticamente obtiene el usuario del store de Redux
 * @returns {Object} - Estado y funciones de permisos
 */
export const useUserPermissions = () => {
    const currentUser = useSelector(state => state.auth.user);
    const [permissions, setPermissions] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Usar el hook completo de gestión de usuarios
      const {
        userPermissions
      } = useUsuariosManagement();
      
      console.log('Permisos de usuario:', userPermissions);

    // Función para cargar permisos
    const loadPermissions = useCallback(async () => {
        if (!currentUser) {
            setPermissions(null);
            setError('Usuario no autenticado');
            return;
        }

        setLoading(true);
        setError(null);
        setPermissions(userPermissions);
        setLoading(false);


        // try {
        //     const response = await getUserPermissions(currentUser);
            
        //     if (response.success) {
        //         const processedPermissions = processUserPermissions(response.data);
        //         setPermissions(processedPermissions);
        //         console.log('✅ Permisos cargados:', processedPermissions);
        //     } else {
        //         setError(response.error);
        //         // console.error('❌ Error cargando permisos:', response.error);
        //     }
        // } catch (err) {
        //     setError('Error inesperado al cargar permisos');
        //     // console.error('❌ Error inesperado:', err);
        // } finally {
        //     setLoading(false);
        // }
    }, [currentUser]);

    // Cargar permisos al montar el componente o cambiar usuario
    useEffect(() => {
        loadPermissions();
    }, [loadPermissions]);

    // Funciones de utilidad
    const hasAccess = useCallback((moduleId) => {
        if (!permissions || !permissions.modules) {
            return false;
        }
        const modulePerms = permissions.modules[moduleId];
        return modulePerms ? modulePerms.hasAccess : false;
    }, [permissions]);

    const hasPermission = useCallback((moduleId, permission) => {
        if (!hasAccess(moduleId)) {
            return false;
        }
        
        const modulePerms = permissions.modules[moduleId];
        
        switch (permission) {
            case 'vista':
                return modulePerms.canView;
            case 'crear':
                return modulePerms.canCreate;
            case 'editar':
                return modulePerms.canEdit;
            case 'eliminar':
                return modulePerms.canDelete;
            case 'administrar':
                return modulePerms.canAdmin;
            case 'aprobar':
                return modulePerms.canApprove;
            case 'solicitar':
                return modulePerms.canRequest;
            default:
                return modulePerms.permissions.includes(permission);
        }
    }, [permissions, hasAccess]);

    const getAccessibleModulesList = useCallback(() => {
        if (!permissions || !permissions.modules) {
            return [];
        }
        
        return Object.keys(permissions.modules).filter(moduleId => 
            permissions.modules[moduleId].hasAccess
        );
    }, [permissions]);

    const canView = useCallback((moduleId) => {
        return hasPermission(moduleId, 'vista');
    }, [hasPermission]);

    const canCreate = useCallback((moduleId) => {
        return hasPermission(moduleId, 'crear');
    }, [hasPermission]);

    const canEdit = useCallback((moduleId) => {
        return hasPermission(moduleId, 'editar');
    }, [hasPermission]);

    const canDelete = useCallback((moduleId) => {
        return hasPermission(moduleId, 'eliminar');
    }, [hasPermission]);

    const canAdmin = useCallback((moduleId) => {
        return hasPermission(moduleId, 'administrar');
    }, [hasPermission]);

    const canApprove = useCallback((moduleId) => {
        return hasPermission(moduleId, 'aprobar');
    }, [hasPermission]);

    const canRequest = useCallback((moduleId) => {
        return hasPermission(moduleId, 'solicitar');
    }, [hasPermission]);

    // Función para refrescar permisos
    const refreshPermissions = useCallback(() => {
        loadPermissions();
    }, [loadPermissions]);

    return {
        // Estado
        permissions,
        loading,
        error,
        
        // Información del usuario
        userEmail: permissions?.email,
        userId: permissions?.userId,
        userUid: permissions?.userUid,
        hasAnyPermissions: permissions?.hasAnyPermissions || false,
        
        // Funciones de verificación
        hasModuleAccess: hasAccess,
        hasModulePermission: hasPermission,
        canView,
        canCreate,
        canEdit,
        canDelete,
        canAdmin,
        canApprove,
        canRequest,
        
        // Utilidades
        getAccessibleModules: getAccessibleModulesList,
        loadPermissions: refreshPermissions,
        
        // Raw data para casos especiales
        rawPermissions: permissions?.rawPermissions,
        moduleDetails: permissions?.modules
    };
};
